import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import cacheManager from '../CacheManager';
import { formatDate } from '../js/dateFormatter';

const PgPagamentos = () => {
  const [clientesByIdCache, setClientesByIdCache] = useState({});
  const [faturasByIdCache, setFaturasByIdCache] = useState({});
  const [pagamentos, setPagamentos] = useState([]);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const pagamentosPorPagina = 10;
  const [termoDeBusca, setTermoDeBusca] = useState('');
  const [pagamentosFiltrados, setPagamentosFiltrados] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const search = async () => {
    try {
      setIsLoading(true);

      const clientesByIdCache = await cacheManager.getClientesById();
      setClientesByIdCache(clientesByIdCache);
      const faturasByIdCache = await cacheManager.getFaturasById();
      setFaturasByIdCache(faturasByIdCache);
      const pagamentosCache = await cacheManager.getPagamentosCache();
      setPagamentos(pagamentosCache);

    } catch (error) {
      console.error('Erro ao buscar pagamentos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Estilos para esmaecer a página
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2, // Garanta que esteja acima de outros elementos da página
    display: isLoading ? 'block' : 'none', // Mostra apenas quando está carregando
  };

  useEffect(() => {
    search(); // Chama a função search ao montar o componente
  }, []); // O array vazio como segundo argumento garante que o efeito seja executado apenas uma vez, na montagem do componente

  useEffect(() => {
    const pagamentosOrdenados = [...pagamentos].sort((a, b) => {
      const idA = a.id_pagamento;
      const idB = b.id_pagamento;

      return idB - idA;
    });

    const pagamentosFiltrados = pagamentosOrdenados.filter(pagamento => {
      let nomeCliente = clientesByIdCache[pagamento.id_cliente]?.nome || '';
      nomeCliente = nomeCliente.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      return nomeCliente.includes(termoDeBusca.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
    });

    setPagamentosFiltrados(pagamentosFiltrados);
    setPaginaAtual(1);
  }, [pagamentos, termoDeBusca]);

  const addPagamento = () => {
    navigate('/pagamento/novo'); // Use navigate para ir para /pagamento/novo
  };

  // Calcular o número total de páginas
  const totalPaginas = Math.ceil(pagamentosFiltrados.length / pagamentosPorPagina);

  // Obter os pagamentos da página atual
  const indexDoUltimoPagamento = paginaAtual * pagamentosPorPagina;
  const indexDoPrimeiroPagamento = indexDoUltimoPagamento - pagamentosPorPagina;
  const pagamentosAtuais = pagamentosFiltrados.slice(indexDoPrimeiroPagamento, indexDoUltimoPagamento);

  // Funções para alterar a página
  const irParaPagina = (numero) => {
    setPaginaAtual(numero);
  };

  return (
    <div className="container mt-5">
      {isLoading && <div style={overlayStyle}></div>}
      <h2 className="mb-4">Pagamentos</h2>
      <div className="input-group mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar..."
          id="search-input"
          value={termoDeBusca}
          onChange={(e) => setTermoDeBusca(e.target.value)}
        />
        <button className="btn btn-outline-secondary" type="button" onClick={search}>Buscar</button>
      </div>
      <table className="table">
        <thead className="table-light">
          <tr style={{ height: 'auto !important', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <th>Nº</th>
            <th>Cliente</th>
            <th>Fatura</th>
            <th>Valor</th>
            <th>Data</th>
            <th>Data do Extorno</th>
            <th>Forma</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {pagamentosAtuais.map((pagamento, index) => (
            <tr key={index} style={{ height: 'auto !important', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <td>
                <Link to={`/pagamento/editar/${pagamento.id_pagamento}`} className="text-dark underline-on-hover">
                  {pagamento.id_pagamento}
                </Link>
              </td>
              <td>{clientesByIdCache[pagamento.id_cliente]?.nome || 'Cliente não encontrado'}</td>
              <td>{pagamento.id_fatura?pagamento.id_fatura : 'Fatura não encontrada'}</td>
              <td>R$ {pagamento.valor}</td>
              <td>{formatDate(pagamento.data_pagamento)}</td>
              <td>{formatDate(pagamento.data_extorno)}</td>
              <td>{pagamento.forma_pagamento}</td>
              <td>
                <Link to={`/pagamento/editar/${pagamento.id_pagamento}`} className="btn btn-primary btn-sm me-2">
                  <FontAwesomeIcon icon={faEdit} /> Editar
                </Link>
                <button className="btn btn-danger btn-sm" disabled>
                  <FontAwesomeIcon icon={faTrash} /> Excluir
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          {/* Conteúdo da paginação */}
          <button onClick={() => irParaPagina(1)} disabled={paginaAtual === 1} className="btn btn-secondary" style={{ fontSize: '10px' }}>
            Primeira
          </button>
          {paginaAtual > 2 && (
            <button onClick={() => irParaPagina(paginaAtual - 2)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual - 2}
            </button>
          )}
          {paginaAtual > 1 && (
            <button onClick={() => irParaPagina(paginaAtual - 1)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual - 1}
            </button>
          )}
          <button className="btn btn-primary" style={{ fontSize: '10px' }}>
            {paginaAtual}
          </button>
          {paginaAtual < totalPaginas && (
            <button onClick={() => irParaPagina(paginaAtual + 1)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual + 1}
            </button>
          )}
          {paginaAtual < totalPaginas - 1 && (
            <button onClick={() => irParaPagina(paginaAtual + 2)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual + 2}
            </button>
          )}
          <button onClick={() => irParaPagina(totalPaginas)} disabled={paginaAtual === totalPaginas} className="btn btn-secondary" style={{ fontSize: '10px' }}>
            Última
          </button>
        </div>
        <div>
          <button type="button" className="btn btn-primary" onClick={addPagamento}>Incluir</button>
        </div>
      </div>
    </div>
  );
};

export default PgPagamentos;